import axios from 'axios';
import store from './store';
import { LOG_OUT_SUCCESS } from '../constants/action-types';
import { API_URLS } from '../constants/config';
import { isAdminAuthPublicUrl } from './adminAuthApi';
import { refreshTokens } from '../actions/usersActions';
import { getTokensFromStore, syncStoredSessionTokens } from './tokens';

let isHandlingUnauthorized = false;
let isRefreshing = false;
let refreshWaiters = [];

const isRefreshRequest = (url = '') => String(url).indexOf(API_URLS.refreshTokens) !== -1;

export const redirectUnauthorizedToHome = () => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;
  store.dispatch({ type: LOG_OUT_SUCCESS });
  localStorage.removeItem('userData');
  localStorage.removeItem('persist:authType');

  if (window.location.pathname !== '/') {
    window.location.assign('/');
  } else {
    isHandlingUnauthorized = false;
  }
};

const flushRefreshWaiters = (error, accessToken) => {
  const waiters = refreshWaiters;
  refreshWaiters = [];
  waiters.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(accessToken);
  });
};

/**
 * 401 на API → один refreshTokens, подставить новую пару и повторить запрос.
 * Logout только если refresh недоступен / тоже вернул 401.
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response && error.response.status;
    const original = error.config;
    const requestUrl = (original && original.url) || '';

    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    // Публичный auth / сам refresh — не зацикливаем
    if (isAdminAuthPublicUrl(requestUrl)) {
      return Promise.reject(error);
    }
    if (isRefreshRequest(requestUrl)) {
      redirectUnauthorizedToHome();
      return Promise.reject(error);
    }

    // Уже пробовали refresh для этого запроса
    if (original._retry) {
      redirectUnauthorizedToHome();
      return Promise.reject(error);
    }
    original._retry = true;

    const existing = getTokensFromStore();
    if (!existing?.refresh_token) {
      redirectUnauthorizedToHome();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push({
          resolve: (accessToken) => {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${accessToken}`;
            resolve(axios(original));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      const refreshed = await store.dispatch(refreshTokens());
      const next = refreshed?.data || {};
      if (!next.access_token) {
        flushRefreshWaiters(error);
        redirectUnauthorizedToHome();
        return Promise.reject(error);
      }
      syncStoredSessionTokens(next);
      flushRefreshWaiters(null, next.access_token);
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${next.access_token}`;
      return axios(original);
    } catch (refreshError) {
      flushRefreshWaiters(refreshError);
      redirectUnauthorizedToHome();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
